import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Groups as CommunityIcon,
  TrendingUp as SignalsIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import { UserProfile, UserRole, AdminPermission, ROLE_CONFIGURATIONS, getRoleColor, canManageUser } from '../types/userTypes';
import roleService from '../services/roleService';
import { useRoles } from '../hooks/useRoles';
import { useAuth } from '../contexts/AuthContext';

interface RoleManagementDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserProfile | null;
  onUserUpdated: () => void;
}

const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case 'system_admin':
      return <AdminIcon />;
    case 'community_admin':
      return <CommunityIcon />;
    case 'signals_admin':
      return <SignalsIcon />;
    default:
      return <UserIcon />;
  }
};

const getPermissionDisplayName = (permission: AdminPermission): string => {
  const names: Record<AdminPermission, string> = {
    user_management: 'User Management',
    system_settings: 'System Settings',
    loyalty_management: 'Loyalty Management',
    reports_analytics: 'Reports & Analytics',
    notifications_management: 'Notifications Management',
    community_management: 'Community Management',
    signals_management: 'Signals Management',
    financial_management: 'Financial Management',
    content_management: 'Content Management',
    product_management: 'Product Management',
    security_management: 'Security Management',
  };
  return names[permission] || permission;
};

export const RoleManagementDialog: React.FC<RoleManagementDialogProps> = ({
  open,
  onClose,
  user,
  onUserUpdated,
}) => {
  const { currentUser } = useAuth();
  const { currentUserRole, hasPermission: currentUserHasPermission } = useRoles();
  
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const [customPermissions, setCustomPermissions] = useState<AdminPermission[]>([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role || 'user');
      setCustomPermissions(user.adminPermissions || []);
      setReason('');
      setError(null);
      setSuccess(null);
    }
  }, [user]);

  const handleRoleChange = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = await roleService.updateUserRole(
        user.uid,
        selectedRole,
        currentUser?.uid || '',
        reason || undefined
      );

      if (result.success) {
        setSuccess('User role updated successfully');
        onUserUpdated();
      } else {
        setError(result.error || 'Failed to update user role');
      }
    } catch (err) {
      setError('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (permission: AdminPermission, isGranting: boolean) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = isGranting
        ? await roleService.grantCustomPermission(user.uid, permission, currentUser?.uid || '', reason || undefined)
        : await roleService.revokeCustomPermission(user.uid, permission, currentUser?.uid || '', reason || undefined);

      if (result.success) {
        setSuccess(`Permission ${isGranting ? 'granted' : 'revoked'} successfully`);
        
        // Update local state
        if (isGranting) {
          setCustomPermissions(prev => [...prev, permission]);
        } else {
          setCustomPermissions(prev => prev.filter(p => p !== permission));
        }
        
        onUserUpdated();
      } else {
        setError(result.error || `Failed to ${isGranting ? 'grant' : 'revoke'} permission`);
      }
    } catch (err) {
      setError(`Failed to ${isGranting ? 'grant' : 'revoke'} permission`);
    } finally {
      setLoading(false);
    }
  };

  const canManageThisUser = user ? canManageUser(currentUserRole, user.role || 'user') : false;
  const canChangeRole = currentUserHasPermission('user_management') && canManageThisUser;

  if (!user) return null;

  const currentRoleConfig = ROLE_CONFIGURATIONS[user.role || 'user'];
  const rolePermissions = currentRoleConfig.permissions;
  const allPermissions: AdminPermission[] = [
    'user_management',
    'system_settings',
    'loyalty_management',
    'reports_analytics',
    'notifications_management',
    'community_management',
    'signals_management',
    'financial_management',
    'content_management',
    'product_management',
    'security_management',
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon />
          <Box>
            <Typography variant="h6">Manage User Roles & Permissions</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.displayName || user.email}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!canChangeRole && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You don't have permission to manage this user's roles.
          </Alert>
        )}

        {/* Current Role Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getRoleIcon(user.role || 'user')}
            Current Role
          </Typography>
          <Chip
            label={currentRoleConfig.displayName}
            sx={{ 
              bgcolor: getRoleColor(user.role || 'user') + '20',
              color: getRoleColor(user.role || 'user'),
              fontWeight: 'bold',
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {currentRoleConfig.description}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Role Change Section */}
        {canChangeRole && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Change Role</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>New Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="New Role"
                    onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                  >
                    {Object.entries(ROLE_CONFIGURATIONS).map(([role, config]) => (
                      <MenuItem 
                        key={role} 
                        value={role}
                        disabled={!canManageUser(currentUserRole, role as UserRole)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(role as UserRole)}
                          <Box>
                            <Typography variant="body2">{config.displayName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {config.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Reason (optional)"
                  multiline
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you're changing this user's role..."
                />

                <Button
                  variant="contained"
                  onClick={handleRoleChange}
                  disabled={loading || selectedRole === (user.role || 'user')}
                  startIcon={loading ? <CircularProgress size={20} /> : <AdminIcon />}
                >
                  Update Role
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Permissions Section */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Role Permissions
          </Typography>
          
          <List dense>
            {rolePermissions.map((permission) => (
              <ListItem key={permission}>
                <ListItemText
                  primary={getPermissionDisplayName(permission)}
                  secondary="Granted by role"
                />
                <Chip size="small" label="Role" color="primary" />
              </ListItem>
            ))}
          </List>

          {customPermissions.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Custom Permissions
              </Typography>
              <List dense>
                {customPermissions.map((permission) => (
                  <ListItem key={permission}>
                    <ListItemText
                      primary={getPermissionDisplayName(permission)}
                      secondary="Custom grant"
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handlePermissionToggle(permission, false)}
                        disabled={!canChangeRole || loading}
                        color="error"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {canChangeRole && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Grant Additional Permissions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {allPermissions
                    .filter(permission => 
                      !rolePermissions.includes(permission) && 
                      !customPermissions.includes(permission)
                    )
                    .map((permission) => (
                      <ListItem key={permission}>
                        <ListItemText
                          primary={getPermissionDisplayName(permission)}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handlePermissionToggle(permission, true)}
                            disabled={loading}
                            color="primary"
                          >
                            <AddIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}; 